// src/features/estoque/components/UploadNotaXML.jsx
import React, { useState, useEffect, useRef } from "react";
import { parseNotaFiscalXML } from "utils/xmlParser";
import { atualizarEstoqueViaXML, observarEstoque, salvarMapeamento } from "services/estoqueService";
import { Button } from "components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "components/ui/card";
import { FileUp, AlertTriangle, CheckCircle2, PackageCheck, Loader2, XCircle } from "lucide-react";

/**
 * Função auxiliar para ler o arquivo como texto em Promise.
 */
const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    // Em caso de erro local na leitura do arquivo
    reader.onerror = (e) => reject(new Error("Erro ao ler arquivo localmente."));
    reader.readAsText(file);
  });
};

const UploadNotaXML = () => {
  // Estado para armazenar a fila de arquivos a serem processados.
  // Formato de cada item: { id, file, name, status, message, parsedData }
  // Status: 'Pendente', 'Processando', 'Sucesso', 'Erro'
  const [filesQueue, setFilesQueue] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  // Estados para o Mapeamento de Produtos
  const [estoqueItems, setEstoqueItems] = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [pendingMappingData, setPendingMappingData] = useState({ fileId: null, items: [] });
  const [mappingSelections, setMappingSelections] = useState({});
  const [mappingCategories, setMappingCategories] = useState({});

  // Fix stale closure para o setTimeout
  const filesQueueRef = useRef(filesQueue);
  useEffect(() => {
    filesQueueRef.current = filesQueue;
  }, [filesQueue]);

  useEffect(() => {
    // Carrega o estoque para popular as comboboxes de mapeamento
    const unsubscribe = observarEstoque((lista) => {
      setEstoqueItems(lista);
    });
    return () => unsubscribe();
  }, []);

  // Manipulador de Seleção de Múltiplos Arquivos
  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    setGlobalError(null);
    const newQueue = [];

    // Processa os arquivos selecionados
    for (const file of selectedFiles) {
      const fileId = Math.random().toString(36).substring(7) + Date.now();
      let status = "Pendente";
      let message = "";
      let parsedData = null;

      // Validação da extensão e do tipo MIME
      const isValidType = file.name.toLowerCase().endsWith(".xml") || 
                          file.type === "text/xml" || 
                          file.type === "application/xml";
      if (!isValidType) {
        status = "Erro";
        message = "Formato inválido (Apenas .xml)";
      } 
      // Validação de Tamanho (Máx 2MB)
      else if (file.size > 2 * 1024 * 1024) {
        status = "Erro";
        message = "Arquivo excede 2MB";
      } else {
        // Tenta fazer o parse localmente antes mesmo de enviar
        try {
          const xmlContent = await readFileAsText(file);
          const result = parseNotaFiscalXML(xmlContent);
          if (result.success) {
            parsedData = result;
          } else {
            status = "Erro";
            message = result.error;
          }
        } catch (err) {
            status = "Erro";
            message = err.message;
        }
      }

      newQueue.push({
        id: fileId,
        file: file,
        name: file.name,
        status,
        message,
        parsedData
      });
    }

    // Adiciona os novos arquivos ao invés de substituir, caso o usuário selecione mais.
    setFilesQueue(prev => [...prev, ...newQueue]);
    
    // Reseta o input para permitir selecionar os mesmos arquivos se precisar
    e.target.value = null; 
  };

  const removerArquivo = (idToRemove) => {
    if (isLoading) return;
    setFilesQueue(prev => prev.filter(f => f.id !== idToRemove));
  };

  // Processo Sequencial em Lote
  // Usamos useCallback para que a função sempre pegue o estado mais atualizado da ref
  const handleAtualizarEstoque = async () => {
    const pendentes = filesQueueRef.current.filter(f => f.status === "Pendente" && f.parsedData);
    if (pendentes.length === 0) return;

    setIsLoading(true);

    // Iteração sequencial (for...of) para processar uma nota por vez e respeitar 
    // os limites do Firestore (500 writes/batch).
    for (const fileItem of pendentes) {
      // Atualiza UI para 'Processando' este arquivo específico
      setFilesQueue(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: "Processando" } : f
      ));

      try {
        const response = await atualizarEstoqueViaXML(fileItem.parsedData);
        
        if (response.pendingMapping) {
            // Se precisar de intervenção humana, aborta o processamento do resto da fila
            // e salva os dados no estado do Modal.
            setFilesQueue(prev => prev.map(f => 
              f.id === fileItem.id ? { 
                ...f, 
                status: "Aguardando Mapeamento", 
                message: response.message 
              } : f
            ));
            setPendingMappingData({
                fileId: fileItem.id,
                items: response.failedItems
            });
            setMappingSelections({});
            setMappingCategories({});
            setShowMappingModal(true);
            setIsLoading(false);
            return; // Interrompe o For Loop
        }

        setFilesQueue(prev => prev.map(f => 
          f.id === fileItem.id ? { 
            ...f, 
            status: response.success ? "Sucesso" : "Erro", 
            message: response.message 
          } : f
        ));
      } catch (error) {
        setFilesQueue(prev => prev.map(f => 
          f.id === fileItem.id ? { 
            ...f, 
            status: "Erro", 
            message: error.message || "Erro desconhecido ao gravar." 
          } : f
        ));
      }
    }

    setIsLoading(false);
  };

  const handleSaveMapping = async () => {
    setIsLoading(true);
    const { items } = pendingMappingData;
    
    try {
        // Salva cada mapeamento no banco de dados
        for (const item of items) {
           const codigoInternoSelecionado = mappingSelections[item.codigoFornecedor];
           const categoriaContabilSelecionada = mappingCategories[item.codigoFornecedor];

           if (!codigoInternoSelecionado || !categoriaContabilSelecionada) {
             throw new Error(`Por favor, preencha o Mapeamento Interno e a Categoria Contábil para o produto: ${item.nomeFornecedor}`);
           }
           await salvarMapeamento(item.cnpjEmitente, item.codigoFornecedor, codigoInternoSelecionado, categoriaContabilSelecionada);
        }

        setShowMappingModal(false);
        setPendingMappingData({ fileId: null, items: [] });
        setMappingSelections({});
        setMappingCategories({});
        
        // Devolve o arquivo bloqueado para 'Pendente' para que o Retry pegue ele
        setFilesQueue(prev => prev.map(f => 
           f.id === pendingMappingData.fileId ? { ...f, status: "Pendente", message: "" } : f
        ));

        // Submete a fila novamente em 1 segundo
        setTimeout(() => {
            handleAtualizarEstoque();
        }, 1000);

    } catch(err) {
        alert(err.message);
        setIsLoading(false);
    }
  };

  // Cálculos de Status Visuais
  const qtdPendentes = filesQueue.filter(f => f.status === "Pendente").length;
  const qtdSucesso = filesQueue.filter(f => f.status === "Sucesso").length;
  const qtdErro = filesQueue.filter(f => f.status === "Erro").length;

  return (
    <Card className="w-full max-w-2xl mx-auto dark:bg-slate-900 border-gray-200 dark:border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUp className="w-6 h-6 text-blue-600" />
          Importação em Lote de Notas (XML)
        </CardTitle>
        <CardDescription>
          Selecione múltiplas Notas Fiscais (NFe/NFCe) para processamento sequencial. O sistema detectará automaticamente Entradas ou Saídas.
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="flex flex-col gap-4">
            {/* Input de Arquivo Customizado Múltiplo */}
            <label className={`block w-full border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${isLoading ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' : 'border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                <span className="text-gray-600 dark:text-gray-400 font-medium">
                   Clique para anexar ou solte os arquivos XML aqui
                </span>
                <input 
                   type="file" 
                   accept=".xml" 
                   multiple
                   disabled={isLoading}
                   onChange={handleFileChange} 
                   className="hidden" 
                />
            </label>

            {globalError && (
                <div className="flex items-center gap-2 bg-red-100 text-red-800 p-3 rounded-md text-sm">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <span>{globalError}</span>
                </div>
            )}

            {/* Painel de Reumo da Fila */}
            {filesQueue.length > 0 && (
              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex justify-between items-center mb-3 text-sm">
                  <span className="font-semibold dark:text-gray-200 flex items-center gap-2">
                    <PackageCheck className="w-4 h-4 text-slate-500" /> 
                    Fila de Processamento ({filesQueue.length})
                  </span>
                  <div className="flex gap-3 text-xs">
                    <span className="text-gray-500">{qtdPendentes} Pendentes</span>
                    <span className="text-green-600">{qtdSucesso} Sucesso</span>
                    <span className="text-red-500">{qtdErro} Erros</span>
                  </div>
                </div>

                <ul className="space-y-2 max-h-60 overflow-y-auto pr-2">
                  {filesQueue.map((item) => (
                    <li key={item.id} className="flex justify-between items-center p-2 rounded relative bg-white dark:bg-slate-800 border dark:border-slate-700 shadow-sm text-sm">
                       <div className="flex flex-col truncate pr-4 max-w-[70%]">
                         <span className="font-medium text-slate-700 dark:text-slate-300 truncate" title={item.name}>{item.name}</span>
                         {/* Exibe mensagem de erro embaixo do nome, se houver */}
                         {item.status === "Erro" && (
                           <span className="text-xs text-red-500 mt-1 truncate" title={item.message}>{item.message}</span>
                         )}
                         {/* Exibe sucesso */}
                         {item.status === "Sucesso" && (
                           <span className="text-xs text-green-600 mt-1 truncate">Estoque atualizado com sucesso.</span>
                         )}
                       </div>

                       <div className="flex items-center gap-3">
                         {item.status === "Pendente" && (
                            <span className="text-xs font-semibold px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-full">
                              Pendente
                            </span>
                         )}
                         {item.status === "Aguardando Mapeamento" && (
                            <span className="text-xs font-semibold px-2 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded-full animate-pulse border border-yellow-300">
                              Ação Necessária
                            </span>
                         )}
                         {item.status === "Processando" && (
                            <span className="flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
                              <Loader2 className="w-3 h-3 animate-spin"/>
                              Proc...
                            </span>
                         )}
                         {item.status === "Sucesso" && (
                            <span className="text-green-600 dark:text-green-500"><CheckCircle2 className="w-5 h-5"/></span>
                         )}
                         {item.status === "Erro" && (
                            <span className="text-red-500 dark:text-red-400"><AlertTriangle className="w-5 h-5"/></span>
                         )}

                         {/* Botão de excluir arquivo da fila (só se não estiver processando) */}
                         {!isLoading && (
                           <button 
                             onClick={() => removerArquivo(item.id)}
                             className="text-slate-400 hover:text-red-500 transition-colors ml-1"
                             title="Remover da lista"
                           >
                              <XCircle className="w-4 h-4" />
                           </button>
                         )}
                       </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      </CardContent>

      <CardFooter className="flex justify-end bg-slate-50 dark:bg-slate-900/50 pt-4 rounded-b-xl border-t dark:border-slate-800">
        <Button 
            disabled={qtdPendentes === 0 || isLoading} 
            onClick={handleAtualizarEstoque}
            className="w-full sm:w-auto"
        >
            {isLoading ? (
               <>
                 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                 Processando Lote...
               </>
            ) : (
               `Confirmar Processamento (${qtdPendentes})`
            )}
        </Button>
      </CardFooter>

      {/* MODAL DE MAPEAMENTO */}
      {showMappingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh]">
            <div className="p-6 border-b dark:border-slate-800">
              <h2 className="text-xl font-bold flex items-center gap-2 text-slate-800 dark:text-white">
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
                Mapeamento de Fornecedor Necessário
              </h2>
              <p className="text-sm border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 p-3 rounded-lg mt-3">
                Alguns produtos desta nota (NFe/NFCe) ainda não estão pareados com o nosso estoque interno.
                Por favor, indique no menu abaixo a qual produto interno cada item do fornecedor corresponde.
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 bg-slate-50 dark:bg-slate-900/50">
               <ul className="space-y-4">
                 {pendingMappingData.items.map((item, index) => (
                    <li key={`${item.codigoFornecedor}-${index}`} className="bg-white dark:bg-slate-800 p-4 rounded-lg border dark:border-slate-700 shadow-sm flex flex-col gap-2">
                       <div className="flex justify-between items-start">
                         <div>
                           <p className="font-semibold text-sm dark:text-slate-200">De: {item.nomeFornecedor}</p>
                           <p className="text-xs text-slate-500">Ref. Fornecedor: {item.codigoFornecedor}</p>
                         </div>
                       </div>
                       
                       <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                              Para o nosso insumo de estoque:
                            </label>
                            <select 
                               className="w-full text-sm border p-2 bg-slate-50 dark:bg-slate-900 dark:text-white rounded-md border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                               value={mappingSelections[item.codigoFornecedor] || ""}
                               onChange={(e) => setMappingSelections({
                                 ...mappingSelections,
                                 [item.codigoFornecedor]: e.target.value
                               })}
                            >
                               <option value="" disabled>--- Selecione ou busque um produto ---</option>
                               {estoqueItems.map((estoq) => (
                                 <option key={estoq.codigo} value={estoq.codigo}>
                                    {estoq.nome} (Código: {estoq.codigo})
                                 </option>
                               ))}
                            </select>
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">
                              Categoria Contábil:
                            </label>
                            <select 
                               className="w-full text-sm border p-2 bg-slate-50 dark:bg-slate-900 dark:text-white rounded-md border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                               value={mappingCategories[item.codigoFornecedor] || ""}
                               onChange={(e) => setMappingCategories({
                                 ...mappingCategories,
                                 [item.codigoFornecedor]: e.target.value
                               })}
                            >
                               <option value="" disabled>--- Selecione a categoria final ---</option>
                               <option value="INSUMO">Insumo</option>
                               <option value="REVENDA">Revenda</option>
                               <option value="PRODUTO_FINAL">Produto Final</option>
                               <option value="USO_CONSUMO">Uso e Consumo</option>
                            </select>
                          </div>
                       </div>
                    </li>
                 ))}
               </ul>
            </div>

            <div className="p-4 border-t dark:border-slate-800 bg-white dark:bg-slate-900 rounded-b-xl flex justify-end gap-3">
               <Button variant="outline" onClick={() => {
                   setShowMappingModal(false);
                   setIsLoading(false);
               }}>
                  Cancelar Lote
               </Button>
               <Button onClick={handleSaveMapping} disabled={isLoading}>
                 {isLoading ? 'Salvando...' : 'Salvar Mapeamentos e Continuar'}
               </Button>
            </div>
          </div>
        </div>
      )}

    </Card>
  );
};

export default UploadNotaXML;
